/**
 * MapThumb — a project's site, drawn as a light street grid (the Led AI design
 * system's `components/brand/MapThumb.jsx`, ported).
 *
 * Deterministic per `seed`, so a project always draws the same streets. It is
 * a stand-in for a real site map: projects carry no coordinates the console
 * can read yet, and a photo tile would be a picture of one walk rather than of
 * the site. The design's own built-in pin is NOT drawn — Home lays its own
 * ~10px ink pin with a white ring over the strip instead (LED-1151).
 */

import { H } from "../tokens";

/** The design system's tiny PRNG, verbatim, so the streets match its tiles. */
function prng(seed: number): () => number {
  let a = seed * 2654435761 + 11;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A stable numeric seed from any id. */
export function mapSeed(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (Math.imul(hash, 31) + id.charCodeAt(i)) | 0;
  return Math.abs(hash % 997) + 3;
}

export function MapThumb({ seed, size = 260 }: { seed: number; size?: number }) {
  const rnd = prng(seed);
  const rot = rnd() * 22 - 11;
  const horiz = [22, 50, 78].map((y) => y + (rnd() * 10 - 5));
  const vert = [22, 50, 78].map((x) => x + (rnd() * 10 - 5));
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
      data-testid="map-thumb"
      style={{ display: "block" }}
    >
      <g transform={`rotate(${rot} 50 50)`}>
        <rect x="-40" y="-40" width="180" height="180" fill={H.mapLand} />
        {horiz.map((y, i) =>
          vert.map((x, j) => (
            <rect
              key={`b${i}-${j}`}
              x={x - 11}
              y={y - 8}
              width="20"
              height="14"
              fill={H.mapBlock}
              opacity="0.8"
            />
          )),
        )}
        {horiz.map((y, i) => (
          <line key={`h${i}`} x1="-40" y1={y} x2="140" y2={y} stroke={H.mapStreet} strokeWidth={i === 1 ? 6 : 4} />
        ))}
        {vert.map((x, i) => (
          <line key={`v${i}`} x1={x} y1="-40" x2={x} y2="140" stroke={H.mapStreet} strokeWidth={i === 1 ? 6 : 4} />
        ))}
      </g>
    </svg>
  );
}
